'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { AlertTriangle, ArrowRight, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';

type Provider = 'fitbit' | 'googlefit' | 'health-connect';

interface ReauthFlag {
  provider: Provider;
  reason?: string;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  fitbit: 'Fitbit',
  googlefit: 'Google Fit',
  'health-connect': 'Health Connect',
};

// Only Fitbit + Google Fit have server-tracked needsReauth flags (OAuth
// token rot). Health Connect uses OS-managed grants — see the separate
// effect below that polls checkHealthPermissions().
const PROVIDERS: Provider[] = ['fitbit', 'googlefit'];

/**
 * Subscribe to each `users/{uid}/wearableConnections/{provider}` doc and surface
 * a banner when any provider has `needsReauth: true`. Click goes to /settings,
 * where the reconnect flow lives.
 *
 * Mount once near the top of the layout. The banner is a no-op until at least
 * one provider goes stale, so it adds zero visual noise during the happy path.
 */
export default function WearableReauthBanner() {
  const { user } = useAuth();
  const [reauths, setReauths] = useState<ReauthFlag[]>([]);
  const [dismissed, setDismissed] = useState<Set<Provider>>(new Set());
  // Cached resolved health source ('health-connect' | 'apple-health' |
  // 'google-fit-web' | 'google-fit-fallback' | 'none' | null while loading).
  // Used to suppress the Google Fit reauth nag for users who switched to
  // Health Connect — they're not using GF, the CF will keep failing GF
  // token refreshes forever, but the banner shouldn't pester them about
  // a service they don't use.
  const [healthSource, setHealthSource] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { detectHealthSource } = await import('@/lib/healthData');
        const src = await detectHealthSource();
        if (!cancelled) setHealthSource(src);
      } catch {
        if (!cancelled) setHealthSource('none');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setReauths([]);
      return;
    }

    let cancelled = false;
    const unsubs: Array<() => void> = [];

    (async () => {
      const { doc, onSnapshot } = await import('firebase/firestore');
      const { db } = await import('@/lib/firebase');

      for (const provider of PROVIDERS) {
        const ref = doc(db, 'users', user.uid, 'wearableConnections', provider);
        const unsub = onSnapshot(
          ref,
          (snap) => {
            if (cancelled) return;
            const data = snap.data();
            const needsReauth = data?.isConnected && data?.needsReauth === true;
            // Suppress the Google Fit nag when the user is on Health
            // Connect or Apple Health. Their GF refresh token is dead
            // and they don't need it — the banner would just nag
            // forever.
            const suppress = provider === 'googlefit' &&
              (healthSource === 'health-connect' || healthSource === 'apple-health');
            setReauths((prev) => {
              const others = prev.filter((r) => r.provider !== provider);
              return needsReauth && !suppress
                ? [...others, { provider, reason: data?.needsReauthReason }]
                : others;
            });
          },
          () => {
            // Permission/network errors are non-critical; just don't show the banner.
          }
        );
        unsubs.push(unsub);
      }
    })();

    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
    };
  }, [user?.uid, healthSource]);

  // ─── Health Connect permission state ─────────────────────────────────
  // Detect the post-update gap: user had HC connected on an older app
  // version, then we widened the read scope (v1.9.0 added HRV / RHR /
  // sleep / weight / body fat) and Android revoked grants because the
  // app's declared permissions changed. They'd never see a re-prompt
  // unless something nudges them — this banner is the nudge. CTA calls
  // requestHealthPermissions inline (one tap → system UI → back).
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;

        // Only show the banner when the user previously connected HC.
        // First-time-ever users belong in the Recovery page's explicit
        // connect flow, not a "reconnect" banner that implies they had
        // it before.
        const { isWearableConnected } = await import('@/lib/wearableConnectionService');
        const wasConnected = await isWearableConnected(user.uid, 'health-connect')
          || await isWearableConnected(user.uid, 'googlefit'); // legacy alias
        if (cancelled || !wasConnected) return;

        const { HealthConnect } = await import('@kiwi-health/capacitor-health-connect');
        const perms = await HealthConnect.checkHealthPermissions({
          read: [
            'Steps', 'Distance', 'ActiveCaloriesBurned',
            'HeartRateSeries', 'RestingHeartRate', 'HeartRateVariabilityRmssd',
            'SleepSession', 'Weight', 'BodyFat',
          ] as any,
          write: [],
        });
        if (cancelled) return;
        const grantedCount = perms?.grantedPermissions?.length ?? 0;
        if (grantedCount === 0) {
          setReauths(prev => {
            const others = prev.filter(r => r.provider !== 'health-connect');
            return [...others, {
              provider: 'health-connect',
              reason: 'Permissions were reset on this update. One tap re-grants and your data resumes syncing.',
            }];
          });
        } else {
          setReauths(prev => prev.filter(r => r.provider !== 'health-connect'));
        }
      } catch (err) {
        // Plugin unavailable, HC not installed, or precheck failed —
        // banner stays hidden. The user can still use the explicit
        // Recovery-page connect flow.
        console.warn('[reauth-banner] HC precheck failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // One-tap HC re-grant. Fires the system permission UI; on return we
  // re-check and either dismiss the banner or leave it (user denied).
  const [hcReconnecting, setHcReconnecting] = useState(false);
  const handleHcReconnect = useCallback(async () => {
    if (hcReconnecting) return;
    setHcReconnecting(true);
    try {
      const { HealthConnect } = await import('@kiwi-health/capacitor-health-connect');
      const result = await HealthConnect.requestHealthPermissions({
        read: [
          'Steps', 'Distance', 'ActiveCaloriesBurned',
          'HeartRateSeries', 'RestingHeartRate', 'HeartRateVariabilityRmssd',
          'SleepSession', 'Weight', 'BodyFat',
        ] as any,
        write: [],
      });
      if ((result?.grantedPermissions?.length ?? 0) > 0) {
        setReauths(prev => prev.filter(r => r.provider !== 'health-connect'));
        // Trigger a follow-up sync so the user sees fresh data immediately.
        try {
          const { syncHealthData } = await import('@/lib/healthData');
          if (user?.uid) await syncHealthData(user.uid);
        } catch {}
      }
    } catch (err) {
      console.warn('[reauth-banner] HC reconnect failed:', err);
    } finally {
      setHcReconnecting(false);
    }
  }, [hcReconnecting, user?.uid]);

  const visible = reauths.filter((r) => !dismissed.has(r.provider));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-2">
      {visible.map((r) => (
        <div
          key={r.provider}
          className="flex items-start gap-3 border-l-4 border-amber-400 bg-amber-950/40 text-amber-100 rounded p-3"
          role="alert"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-300" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {PROVIDER_LABELS[r.provider]} needs reconnecting
            </p>
            <p className="text-xs text-amber-200/80 mt-0.5">
              {r.reason ?? 'Authorization expired — your activity data has stopped syncing.'}
            </p>
          </div>
          {r.provider === 'health-connect' ? (
            <button
              onClick={handleHcReconnect}
              disabled={hcReconnecting}
              className="inline-flex items-center gap-1 text-xs font-bold text-amber-200 hover:text-white disabled:opacity-50 whitespace-nowrap"
            >
              {hcReconnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
              {!hcReconnecting && <ArrowRight className="w-3 h-3" aria-hidden />}
            </button>
          ) : (
            <Link
              href={`/settings#${r.provider}`}
              className="inline-flex items-center gap-1 text-xs font-bold text-amber-200 hover:text-white whitespace-nowrap"
            >
              Reconnect
              <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
          )}
          <button
            onClick={() => setDismissed((prev) => new Set(prev).add(r.provider))}
            className="text-amber-300/60 hover:text-amber-100 flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
